<template>
  <div class="map-page">
    <div class="map-shell">

      <!-- Map actions -->
      <div class="map-actions">
        <div class="left-actions">
          <BaseButton
            @click="toggleFilters"
            :class="['nav-btn', { 'active-full': filtersOpen }]"
           :aria-pressed="filtersOpen">Фильтры
          </BaseButton>

          <!-- Новая кнопка: показать только образцы с полным набором -->
          <BaseButton
            :class="['nav-btn', { 'active-full': showFullSamples }]"
            @click="toggleShowFullSamples"
          >
            {{ showFullSamples ? 'Показать все образцы' : 'Показать образцы с полным набором данных' }}
          </BaseButton>
        </div>
      </div>

      <!-- Filters drawer -->
      <FiltersDrawer
        v-model:open="filtersOpen"
        :draft="draftFilters"
        :applied="currentFilters"
        :sample-count="previewCount"
        :active-detail-key="detailFilterKey"
        :detail-open="detailPanelOpen"
        @draft-changed="onDraftChanged"
        @open-detail="openFilterDetail"
        @apply-all="applyAllDrafts"
        @reset-all="resetAllDrafts"
      />

      <!-- Map: передаём теперь mapIds (массив id) а не объекты points -->
      <MapContainer
        :idlist="effectiveMapIds"
        :center="mapCenter"
        :zoom="mapZoom"
        @point-click="onPointClick"
      />

      <!-- Right detail panel -->
      <FilterDetailPanel
        v-if="detailPanelOpen && detailFilterKey"
        :filterKey="detailFilterKey"
        :filterLabel="detailFilterLabel"
        :options="detailOptions"
        :position="detailPanelPosition"
        :initialSelected="draftFilters[detailFilterKey] || []"
        @apply="onDetailApply"
        @clear="onDetailClear"
        @close="() => { detailPanelOpen = false; detailFilterKey = null }"
      />

      <ItemsDrawer
        v-model:open="itemsDrawerOpen"
        :items="itemsDrawerItems"
        :selected-item="selectedItem"
        @select-item="onSelectItemFromDrawer"
      />

      <ItemDetailPanel
        v-if="itemDetailOpen && selectedItem"
        :item="selectedItem"
        :position="itemDetailPosition"
        @close="itemDetailOpen = false"
      />
    </div>
  </div>
</template>

<script>
import MapContainer from '@/components/map/MapContainer.vue'
import FiltersDrawer from '@/components/map/FiltersDrawer.vue'
import FilterDetailPanel from '@/components/map/FilterDetailPanel.vue'
import BaseButton from '@/components/ui/BaseButton.vue'
import api from '@/services/api'
import ItemsDrawer from '@/components/map/ItemsDrawer.vue'
import ItemDetailPanel from '@/components/map/ItemDetailPanel.vue'

const NUMERIC_RANGE_KEYS = ['resolution', 'size_mm', 'voltage', 'porosity', 'permeability']

function parseNumericValue(v) {
  if (v === null || v === undefined) return null
  const s = String(v).trim()
  if (s === '') return null
  const normalized = s.replace('%','').replace(/\s+/g,'').replace(',', '.')
  const n = Number(normalized)
  return Number.isFinite(n) ? n : null
}

// helper: извлечь "атомарные" значения текстового поля (в нижнем регистре)
function extractTextValues(item, key) {
  const raw = item.__raw ?? item.raw ?? item
  // попробуем взять значение по ключу (первый ненулевой)
  let val = raw[key] ?? item[key] ?? null
  if (val === null || val === undefined) return []

  // если значение уже массив — просто нормализуем элементы
  if (Array.isArray(val)) {
    return val
      .map(v => (v === null || v === undefined ? '' : String(v).trim()))
      .filter(Boolean)
      .map(s => s.toLowerCase())
  }

  // если это не строка — привести в строку
  const s = String(val)

  // разбиваем по частым разделителям: точное требование у вас — ';', но добавим ещё ',' '/' '|' и т.п.
  // также поддержим китайский/полуширокий символы '；' и '、'
  const parts = s.split(/[;\/,|、；]+/).map(p => p.trim()).filter(Boolean)

  // опционально — можно убрать содержимое в скобках, но аккуратно. Для простоты — тримим.
  return parts.map(p => p.toLowerCase())
}

function getNumericFieldValue(item, k) {
  const raw = item.__raw ?? item.raw ?? item
  let cand = raw[k]
  if (cand !== undefined && cand !== null && cand !== '') return parseNumericValue(cand)

  const alternatives = {
    size_mm: ['size', 'size_mm', 'size_mm_value', 'size-mm', 'size(mm)', 'size mm'],
    resolution: ['resolution', 'resolution_um', 'res', 'res_um'],
    voltage: ['voltage', 'kv', 'kV'],
    porosity: ['porosity', 'porosity_%', 'porosity_value'],
    permeability: ['permeability', 'perm', 'k'],
  }

  const alts = alternatives[k] || []
  for (const a of alts) {
    if (raw[a] !== undefined && raw[a] !== null && raw[a] !== '') {
      const p = parseNumericValue(raw[a])
      if (p !== null) return p
    }
  }

  const camel = k.replace(/_([a-z])/g, (_, ch) => ch.toUpperCase())
  if (raw[camel] !== undefined) {
    const p = parseNumericValue(raw[camel])
    if (p !== null) return p
  }

  return null
}

export default {
  name: 'MapView',
  components: { MapContainer, FiltersDrawer, FilterDetailPanel, BaseButton, ItemsDrawer, ItemDetailPanel },
  data() {
    return {
      items: [],
      columns: [],
      currentFilters: {},
      draftFilters: {},
      searchQuery: '',
      filtersOpen: false,
      detailPanelOpen: false,
      detailFilterKey: null,
      detailFilterLabel: null,
      detailOptions: [],
      detailPanelPosition: null,
      mapCenter: [60, 56],
      mapZoom: 4,

      // items drawer state
      itemsDrawerOpen: false,
      itemsDrawerItems: [],
      selectedItem: null,
      itemDetailOpen: false,
      itemDetailPosition: null,

      // --- new: full-sample mode ---
      showFullSamples: false,
      // <-- ЗАМЕНИТЕ ЗДЕСЬ на реальные id ваших 4 образцов (строки) -->
      fullSampleIds: [
        'ABB_001',
        'ADU_001',
        'AFT_019',
        'AOH_001',
        'AOV_003',
        'AYP_154',
        'BBQ_001',
        'BEE_001'
      ],
    }
  },
  computed: {
    mergedFilters() {
      return { ...this.currentFilters, ...this.draftFilters }
    },
    previewFilteredItems() {
      if (!this.items || !this.items.length) return []
      const filters = this.mergedFilters
      return this.items.filter(item => {
        for (const [k, v] of Object.entries(filters)) {
          if (v === null || v === undefined) continue
          if (Array.isArray(v) && v.length === 0) continue

          if (Array.isArray(v) && v.length === 2 && NUMERIC_RANGE_KEYS.includes(k)) {
            const val = getNumericFieldValue(item, k)
            const min = parseNumericValue(v[0])
            const max = parseNumericValue(v[1])
            if (min !== null && (val === null || val < min)) return false
            if (max !== null && (val === null || val > max)) return false
            continue
          }

          if (k === 'datalist') {
            const dl = String(item.datalist ?? '').toLowerCase()
            for (const sel of v) if (!dl.includes(String(sel).toLowerCase())) return false
            continue
          }

          if (Array.isArray(v)) {
            // special handling for multi-valued text fields: if item contains a list (semicolon-separated etc.)
            const itemVals = extractTextValues(item, k) // массив lower-case
            // if user selected array v (array of chosen options) -> check any selected value present in item values
            if (!v.some(sel => itemVals.includes(String(sel).toLowerCase()))) return false
          } else {
            if (String(item[k] ?? '').toLowerCase() !== String(v).toLowerCase()) return false
          }
        }
        return true
      })
    },
    previewCount() { return this.previewFilteredItems.length },

    points() {
      const isDev = (typeof process !== 'undefined' && process && process.env && process.env.NODE_ENV !== 'production')
                    || (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.MODE !== 'production')
      const logDebug = (...a) => { if (isDev) console.debug(...a) }

      const grouped = {}
      const filteredItems = this.items.filter(item => {
        for (const [k, v] of Object.entries(this.currentFilters)) {
          if (v === null || v === undefined) continue
          if (Array.isArray(v) && v.length === 0) continue

          if (Array.isArray(v) && v.length === 2 && NUMERIC_RANGE_KEYS.includes(k)) {
            const val = getNumericFieldValue(item, k)
            const min = parseNumericValue(v[0])
            const max = parseNumericValue(v[1])
            if (min !== null) {
              if (val === null || val < min) return false
            }
            if (max !== null) {
              if (val === null || val > max) return false
            }
            continue
          }

          if (k === 'datalist') {
            const dl = String(item.datalist ?? '').toLowerCase()
            for (const sel of v) if (!dl.includes(String(sel).toLowerCase())) return false
            continue
          }

          if (Array.isArray(v)) {
            const itemVals = extractTextValues(item, k)
            if (!v.some(sel => itemVals.includes(String(sel).toLowerCase()))) return false
          } else {
            if (String(item[k] ?? '').toLowerCase() !== String(v).toLowerCase()) return false
          }
        }
        return true
      })

      const pickCoords = (it) => {
        const raw = it.__raw || it.raw || it
        const latCandidate = raw.latitude ?? raw.lat ?? raw.y_coord ?? raw.y ?? raw['y coord'] ?? raw.Y ?? raw.latitude_deg
        const lonCandidate = raw.longitude ?? raw.lon ?? raw.x_coord ?? raw.x ?? raw['x coord'] ?? raw.X ?? raw.longitude_deg

        const parse = v => {
          if (v === null || v === undefined || v === '') return null
          const n = Number(String(v).replace(',', '.'))
          return isNaN(n) ? null : n
        }
        const lat = parse(latCandidate)
        const lon = parse(lonCandidate)
        if (lat === null || lon === null) return null
        return { lat, lon }
      }

      const samplePreview = []
      for (let idx = 0; idx < filteredItems.length; idx++) {
        const it = filteredItems[idx]
        const coords = pickCoords(it)
        if (!coords) {
          logDebug('[MapView] points() - skipping item without valid coords (showing keys):', Object.keys(it.__raw || it.raw || it))
          continue
        }
        const key = `${coords.lat.toFixed(6)}|${coords.lon.toFixed(6)}`
        if (!grouped[key]) grouped[key] = { lon: coords.lon, lat: coords.lat, items: [] }
        grouped[key].items.push(it)

        if (samplePreview.length < 6) {
          samplePreview.push({
            idx,
            keys: Object.keys(it.__raw || it.raw || it),
            latitude: coords.lat,
            longitude: coords.lon,
            raw: it.__raw || it.raw || it
          })
        }
      }

      logDebug('[MapView] points() - items.length:', this.items ? this.items.length : 0, 'currentFilters:', this.currentFilters)
      logDebug('[MapView] points() - filteredCount:', filteredItems.length)
      if (samplePreview.length) logDebug('[MapView] points() - sample filtered items (first up to 6):', samplePreview)
      logDebug('[MapView] points() - result points count:', Object.keys(grouped).length)

      return Object.values(grouped).map(g => ({ id: `${g.lat}_${g.lon}`, lon: g.lon, lat: g.lat, count: g.items.length, items: g.items }))
    },

    mapIds() {
      const ids = []
      for (const p of this.points) {
        if (!p.items || !Array.isArray(p.items)) continue
        for (const it of p.items) {
          const raw = it.__raw || it.raw || it
          const idVal = raw.internal_id ?? raw.renamed_id ?? raw.id ?? raw.pk ?? it.internal_id ?? it.renamed_id ?? it.id
          if (idVal !== undefined && idVal !== null && idVal !== '') ids.push(String(idVal))
        }
      }
      const unique = Array.from(new Set(ids))
      const isDev = (typeof process !== 'undefined' && process && process.env && process.env.NODE_ENV !== 'production')
                    || (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.MODE !== 'production')
      if (isDev) console.debug('[MapView] mapIds (unique):', ' ', 'raw points count:', ' ')
      return unique
    },

    // --- новый computed: effectiveMapIds ---
    effectiveMapIds() {
      // если режим включен — показываем только спец. список
      if (this.showFullSamples) {
        // убедимся, что возвращаем массив строк (MapContainer ожидает строки)
        return Array.isArray(this.fullSampleIds) ? this.fullSampleIds.map(String) : []
      }
      return this.mapIds
    }
  },

  methods: {
    toggleFilters(evt) {
      try { if (evt && typeof evt.stopPropagation === 'function') evt.stopPropagation() } catch(e){}
      const willOpen = !this.filtersOpen
      this.filtersOpen = willOpen

      if (willOpen) {
        this.itemsDrawerOpen = false
        this.itemDetailOpen = false
        this.detailPanelOpen = false
        this.detailFilterKey = null
      } else {
        this.detailPanelOpen = false
        this.detailFilterKey = null
      }

    },

    toggleShowFullSamples() {
      this.showFullSamples = !this.showFullSamples

      // Если включаем режим — закроем боковые панели и постараемся центрировать карту
      if (this.showFullSamples) {
        this.filtersOpen = false
        this.itemsDrawerOpen = false
        this.itemDetailOpen = false
        this.detailPanelOpen = false
        this.detailFilterKey = null
        this.$nextTick(() => {
          this.centerOnFullSamples()
        })
      } else {
        // вернули обычный режим — можно оставить текущий центр
      }
    },

    centerOnFullSamples() {
      // попытка найти координаты для первой записи в fullSampleIds внутри this.items
      if (!Array.isArray(this.items) || !this.items.length) return
      for (const fid of this.fullSampleIds) {
        const item = this.items.find(it => {
          const raw = it.__raw || it.raw || it
          const idVal = raw.internal_id ?? raw.renamed_id ?? raw.fadr ?? raw.id ?? it.internal_id ?? it.renamed_id ?? it.id
          return String(idVal) === String(fid)
        })
        if (item) {
          // вытащим координаты
          const raw = item.__raw || item.raw || item
          const latCandidate = raw.latitude ?? raw.lat ?? raw.y_coord ?? raw.y ?? raw['y coord'] ?? raw.Y ?? raw.latitude_deg
          const lonCandidate = raw.longitude ?? raw.lon ?? raw.x_coord ?? raw.x ?? raw['x coord'] ?? raw.X ?? raw.longitude_deg
          const parse = v => {
            if (v === null || v === undefined || v === '') return null
            const n = Number(String(v).replace(',', '.'))
            return isNaN(n) ? null : n
          }
          const lat = parse(latCandidate)
          const lon = parse(lonCandidate)
          if (lat !== null && lon !== null) {
            this.mapCenter = [lon, lat]
            this.mapZoom = Math.max(this.mapZoom, 8)
            return
          }
        }
      }
      // если координат нет — ничего не делаем
    },

    onDraftChanged(payload) {
      if (!payload || !payload.key) return
      this.draftFilters = { ...this.draftFilters, [payload.key]: payload.value }
    },

    applyAllDrafts() {
      this.currentFilters = { ...this.draftFilters }
      this.detailPanelOpen = false
      this.detailFilterKey = null
    },

    resetAllDrafts() {
      this.draftFilters = {}
      this.currentFilters = {}
    },

    openFilterDetail(payload) {
      if (!payload || !payload.key) return

      this.detailFilterKey = payload.key
      this.detailFilterLabel = payload.label || payload.key
      this.detailOptions = payload.options || []
      this.detailPanelOpen = true

      if (this.draftFilters[payload.key] === undefined) {
        this.draftFilters = { ...this.draftFilters, [payload.key]: [] }
      }

      this.$nextTick(() => {
        try {
          const shellEl = this.$el.querySelector('.map-shell')
          const drawerEl = this.$el.querySelector('.filters-drawer')

          if (!shellEl || !drawerEl) {
            this.detailPanelPosition = { right: '24px', top: '140px', width: '360px' }
            return
          }

          const shellRect = shellEl.getBoundingClientRect()
          const dRect = drawerEl.getBoundingClientRect()

          const left = Math.round(dRect.right - shellRect.left + 12)
          const top = Math.max(Math.round(dRect.top - shellRect.top + 8), 16)
          const panelWidth = 360
          let finalLeft = left

          if (finalLeft + panelWidth > shellRect.width - 8) {
            finalLeft = Math.max(8, shellRect.width - panelWidth - 8)
          }

          this.detailPanelPosition = { left: `${finalLeft}px`, top: `${top}px`, width: `${panelWidth}px` }
        } catch (e) {
          this.detailPanelPosition = { right: '24px', top: '140px', width: '360px' }
        }
      })
    },

    onDetailApply(payload) {
      if (!payload || !payload.key) return
      this.draftFilters = { ...this.draftFilters, [payload.key]: payload.value }
      this.detailPanelOpen = false
      this.detailFilterKey = null
    },

    onDetailClear(payload) {
      if (!payload || !payload.key) return
      const { [payload.key]: removed, ...rest } = this.draftFilters
      this.draftFilters = rest
    },

    async fetchAllItems() {
      try {
        if (api && api.get) {
          const data = await api.get('/items/?limit=10000')
          this.items = Array.isArray(data) ? data : (data.items || [])
        } else {
          const res = await fetch('/api/v1/items/?limit=10000')
          this.items = await res.json()
        }
      } catch (e) { console.error(e); this.items = [] }
    },
    async fetchColumns() {
      try {
        if (api && api.get) this.columns = await api.get('/headers/')
        else {
          const res = await fetch('/api/v1/headers/')
          this.columns = await res.json()
        }
      } catch(e) { console.warn(e) }
    },

    onPointClick(payload) {
      if (!payload) {
        this.itemsDrawerOpen = false
        this.itemsDrawerItems = []
        this.selectedItem = null
        this.itemDetailOpen = false
        return
      }

      this.itemDetailOpen = false
      this.selectedItem = null

      this.mapCenter = [payload.lon, payload.lat]
      this.mapZoom = Math.max(this.mapZoom, 8)

      this.itemsDrawerItems = payload.items || []
      this.itemsDrawerOpen = true
      this.filtersOpen = false
      this.detailPanelOpen = false
      this.detailFilterKey = null
    },

    onSelectItemFromDrawer(item) {
      this.selectedItem = item
      this.itemDetailOpen = true
      this.$nextTick(() => {
        try {
          const shellRect = this.$el.querySelector('.map-shell').getBoundingClientRect()
          const drawerEl = this.$el.querySelector('.items-drawer')
          if (!drawerEl || !shellRect) { this.itemDetailPosition = { right: '24px', top: '140px', width: '360px' }; return }
          const dRect = drawerEl.getBoundingClientRect()
          const left = Math.round(dRect.right - shellRect.left + 12)
          const top = Math.max(Math.round(dRect.top - shellRect.top + 8), 16)
          const panelWidth = 360
          let finalLeft = left
          if (finalLeft + panelWidth > shellRect.width - 8) finalLeft = Math.max(8, shellRect.width - panelWidth - 8)
          this.itemDetailPosition = { left: `${finalLeft}px`, top: `${top}px`, width: `${panelWidth}px` }
        } catch (e) {
          this.itemDetailPosition = { right: '24px', top: '140px', width: '360px' }
        }
      })
    },
  },
  mounted() {
    this.fetchAllItems(); this.fetchColumns()
  }
}
</script>

<style scoped>
.map-page { min-height:100vh; display:flex; flex-direction:column; }
.map-shell { position: relative; flex: 1; display:flex; align-items:stretch; overflow:hidden; }
.map-actions { position:absolute; top: calc(var(--header-height,64px) + 12px); left:24px; z-index:1600; display:flex; gap:10px }

/* активный стиль кнопки */
.map-actions .nav-btn.active-full {
  background: #222222;
  color: #fff;
  border-color: #222222;
}

.left-actions {
  display: flex;
  gap: 12px;
  align-items: center;
}

</style>
